import React, { useEffect, useRef, useState } from "react";

import { useIntl } from "react-intl";

import { ObservabilityPipelineHttpMetricsOverview } from "Api/observability/pipeline/types";
import InternalTag from "Components/InternalTag";
import { debounce } from "ds/utils/debounce";
import useDecodedParams from "Hooks/useDecodedParams";
import {
  selectFeatureDeploymentOverlayEnabled,
  selectFeatureUsageBasedBillingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import { defaultPollTime } from "src/organization/pages/project/environment/metrics/resources/settings";
import { useAppSelector } from "Store/hooks";

import usePinnedTabsSync from "../hooks/usePinnedTabsSync";
import usePollActivities from "../hooks/usePollActivities";
import useStickyTimecontrol from "../hooks/useStickyTimecontrol";
import TrafficInfo from "../http/components/trafficInfo/TrafficInfo";
import { parseTimeDuration } from "../utils/parseDimensions/parseDimensions";

import { HttpBandWidth } from "./bandwidth";
import { HttpMetricsContext } from "./context";
import useHttpMetricsPoll from "./hook/useHttpMetricsPoll";
import TopUrlView from "./impact/timerangeChart";
import PageSkeleton from "./PageSkeleton";
import { HttpRequest } from "./request";
import * as S from "./style";
import { CursorDataType, PageTimeFrameType } from "./types";

import type { Activity } from "@packages/client";

const Page = () => {
  const isDeploymentOverlayEnabled = useAppSelector(
    selectFeatureDeploymentOverlayEnabled
  );
  const usageBilling = useAppSelector(selectFeatureUsageBasedBillingEnabled);

  const initialTimeframe = useInitialTimeframe();
  const [timeframe, setTimeFrame] =
    useState<PageTimeFrameType>(initialTimeframe);

  const intl = useIntl();
  const [deployment, setDeployment] = useState(isDeploymentOverlayEnabled);
  const [metricsData, setMetricsData] = useState<
    ObservabilityPipelineHttpMetricsOverview | null | undefined
  >(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isUpdating, onLoadingChange] = useState(true);
  const [isSkeletonVisible, setShowSkeleton] = useState(true);
  const [cursorData, setCursorData] = useState<CursorDataType>();

  useEffect(() => {
    // TODO disabled feature until API finds
    // a way to normalise metrics for all time range
    //  !timeframe.zoomIn && setShowSkeleton(true);
    setShowSkeleton(true);
  }, [timeframe]);

  useEffect(() => {
    if (!isUpdating && metricsData !== undefined) {
      setShowSkeleton(false);
    }
  }, [isUpdating, metricsData]);

  const pollInterval = parseTimeDuration(
    initialTimeframe.poll || defaultPollTime[timeframe.id || "fallback"]
  );

  useHttpMetricsPoll({
    timeframe,
    interval: pollInterval.seconds,
    onChange: (data = undefined) => {
      setMetricsData(data);
      setLastUpdated(new Date());
    },
    onError: () => setMetricsData(null),
    onLoadingChange
  });
  const { organizationId, projectId } = useDecodedParams();

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );
  const isAutoscalingEnabled = capabilites?.autoscaling?.enabled;

  usePollActivities({
    enabled: deployment || isAutoscalingEnabled,
    timeframe,
    onChange: setActivities,
    interval: pollInterval.seconds
  });

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    timeframe,
    setTimeframe: setTimeFrame
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeframe);
  };

  const debouncedSetCursorData = debounce(setCursorData);
  const headerRef = useRef<HTMLDivElement>(null);
  useStickyTimecontrol({ ref: headerRef });

  return (
    <HttpMetricsContext.Provider
      value={{
        cursorData,
        setCursorData: debouncedSetCursorData,
        data: metricsData,
        timeframe,
        setTimeFrame,
        activities: deployment || isAutoscalingEnabled ? activities : []
      }}
    >
      <div>
        <S.HeaderSection ref={headerRef}>
          <S.PageTitle tag="h3">
            {intl.formatMessage({ id: "http_traffic2" })}
            {!usageBilling && <InternalTag />}
          </S.PageTitle>

          <S.ChartFilterContainer>
            <TimeframeControl
              isUpdating={isUpdating}
              pollInterval={pollInterval}
              lastUpdated={lastUpdated}
              deployment={{
                enabled: deployment,
                onChange: setDeployment
              }}
              timeframe={{
                timeframe: timeframe,
                onChange: setTimeFrame
              }}
              tabSync={{
                onSyncChange: handleSyncChange,
                isSynced,
                canJoinSync
              }}
            />
          </S.ChartFilterContainer>
        </S.HeaderSection>
        <PageSkeleton isLoading={isSkeletonVisible}>
          <TrafficInfo />
          <S.RequestBandWidthContainer>
            <S.RequestWrapper>
              <HttpRequest data={metricsData} />
            </S.RequestWrapper>
            <hr className="divider" />
            <S.BandWidthWrapper>
              <HttpBandWidth data={metricsData} />
            </S.BandWidthWrapper>
          </S.RequestBandWidthContainer>

          <TopUrlView data={metricsData} />
        </PageSkeleton>
      </div>
    </HttpMetricsContext.Provider>
  );
};

export default Page;
